import { sortBy } from 'lodash-es';
import { useQuery, UseQueryResult } from 'react-query';

import { CONFIG } from '../constants/mapConfig';

export type SearchedLocation = {
  id: string;
  name: string;
  address: string;
  longitude: number;
  latitude: number;
};

type MapboxFeature = {
  id: string;
  center: [number, number];
  place_name: string;
  text: string;
};

type MapBoxResponse = {
  features: MapboxFeature[];
};

export const useSearch = (
  address: string,
  countries: string[] = ['us', 'ca'],
  lang = 'en'
): UseQueryResult<SearchedLocation[], Error> => {
  const searchParams = new URLSearchParams();
  searchParams.append('country', countries.join(','));
  searchParams.append('limit', '15');
  searchParams.append('types', 'region,place,locality');
  searchParams.append('language', lang);
  searchParams.append('access_token', CONFIG.MAPBOX_TOKEN);
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${address}.json?${searchParams.toString()}`;

  return useQuery<MapBoxResponse, Error, SearchedLocation[]>(
    ['location-search', lang, countries, address],
    async () => {
      const response = await fetch(url, {
        method: 'GET',
      });
      return response.json();
    },
    {
      enabled: !!address && address.length > 0,
      select: (data) => {
        return sortBy(
          data.features.map((feature) => {
            const { id, center, place_name, text } = feature;
            const index = (place_name as string).search(',') + 1;
            return {
              id,
              name: text,
              address: (place_name as string).substring(index).trim(),
              longitude: center[0],
              latitude: center[1],
              count: (place_name as string).split(',').length - 1,
            } as SearchedLocation;
          }),
          'count'
        );
      },
      staleTime: 10 * 60 * 1000, // 10 min
      retry: 2,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );
};
